/**
 * Tiny ICU-style messages + Intl wrapper (§15.4 Internationalization). Apps pass message bundles; the kit never
 * ships participant copy of its own beyond fallbacks (participant-safe status language lives in
 * `@windrop/contracts` copy.ts).
 *
 * Supported syntax: `{name}`, `{n, number}`, `{d, date}`, `{d, time}`, `{d, datetime}`,
 * `{n, plural, =0 {none} one {# entry} other {# entries}}`, `{k, select, a {A} other {B}}`.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

export type MessageVars = Record<string, string | number | Date | boolean | null | undefined>;
export type Messages = Record<string, string>;

export interface IntlFormatters {
  locale: string;
  timeZone: string;
  currency?: string;
  t: (key: string, vars?: MessageVars, fallback?: string) => string;
  formatNumber: (n: number | bigint | string, opts?: Intl.NumberFormatOptions) => string;
  /** Money from minor units + ISO-4217 code (D01). Accepts string/bigint minor units; never floats. */
  formatMoney: (
    amountMinor: string | bigint | number,
    currency?: string,
    opts?: { showCode?: boolean },
  ) => string;
  formatDate: (d: Date | string | number, opts?: Intl.DateTimeFormatOptions) => string;
  formatTime: (d: Date | string | number, opts?: Intl.DateTimeFormatOptions) => string;
  /** Full date + time + timezone name, e.g. "12 Nov 2026, 18:00 WAT" (§13.4 Countdown text equivalent). */
  formatDateTime: (d: Date | string | number, opts?: Intl.DateTimeFormatOptions) => string;
  formatRelative: (d: Date | string | number, now?: Date) => string;
  plural: (n: number) => Intl.LDMLPluralRule;
}

export const CURRENCY_EXPONENT: Record<string, number> = {
  XTS: 2,
  NGN: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
  KES: 2,
  GHS: 2,
  ZAR: 2,
  JPY: 0,
  XOF: 0,
};

export function currencyExponent(code: string): number {
  return CURRENCY_EXPONENT[code.toUpperCase()] ?? 2;
}

function toDate(d: Date | string | number): Date {
  return d instanceof Date ? d : new Date(d);
}

/** Formats minor units as a currency string without touching floats until the last Intl step is exact. */
export function formatMoneyMinor(
  amountMinor: string | bigint | number,
  currency: string,
  locale: string,
  opts: { showCode?: boolean } = {},
): string {
  const minor = typeof amountMinor === 'bigint' ? amountMinor : BigInt(String(amountMinor).trim() || '0');
  const exp = currencyExponent(currency);
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const divisor = 10n ** BigInt(exp);
  const major = abs / divisor;
  const frac = abs % divisor;
  const decimal = exp > 0 ? `${major}.${frac.toString().padStart(exp, '0')}` : `${major}`;
  const value = `${negative ? '-' : ''}${decimal}`;
  const code = currency.toUpperCase();
  let known = true;
  try {
    new Intl.NumberFormat(locale, { style: 'currency', currency: code });
  } catch {
    known = false;
  }
  if (code === 'XTS' || !known) {
    // Testing currency / unknown code: numeric formatting + code so demo money is obviously fictional (D-002).
    const nf = new Intl.NumberFormat(locale, { minimumFractionDigits: exp, maximumFractionDigits: exp });
    return `${code} ${nf.format(Number(value))}`;
  }
  const nf = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    currencyDisplay: opts.showCode ? 'code' : 'symbol',
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  });
  // Intl.NumberFormat accepts decimal strings exactly (no float rounding) in modern engines.
  return nf.format(value as unknown as number);
}

// --------------------------------------------------------------------------- ICU-ish message formatting

function splitTopLevel(src: string, sep: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of src) {
    if (ch === '{') depth++;
    if (ch === '}') depth--;
    if (ch === sep && depth === 0) {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

function parseOptions(src: string): Record<string, string> {
  // `=0 {none} one {# entry} other {# entries}`
  const opts: Record<string, string> = {};
  let i = 0;
  while (i < src.length) {
    while (i < src.length && /\s/.test(src[i] ?? '')) i++;
    let key = '';
    while (i < src.length && src[i] !== '{' && !/\s/.test(src[i] ?? '')) key += src[i++];
    while (i < src.length && src[i] !== '{') i++;
    if (i >= src.length) break;
    let depth = 0;
    let body = '';
    do {
      const ch = src[i++] ?? '';
      if (ch === '{') depth++;
      if (ch === '}') depth--;
      if (depth > 0 && !(depth === 1 && ch === '{')) body += ch;
    } while (depth > 0 && i <= src.length);
    if (key) opts[key] = body;
  }
  return opts;
}

export function formatMessage(
  message: string,
  vars: MessageVars = {},
  fmt?: Partial<IntlFormatters>,
): string {
  const locale = fmt?.locale ?? 'en';
  let out = '';
  let i = 0;
  while (i < message.length) {
    const ch = message[i] ?? '';
    if (ch === "'" && message[i + 1] === '{') {
      // ICU escaping: '{' literal
      out += '{';
      i += 2;
      continue;
    }
    if (ch !== '{') {
      out += ch;
      i++;
      continue;
    }
    let depth = 0;
    let inner = '';
    do {
      const c = message[i++] ?? '';
      if (c === '{') depth++;
      if (c === '}') depth--;
      if (depth > 0 && !(depth === 1 && c === '{')) inner += c;
    } while (depth > 0 && i <= message.length);
    const parts = splitTopLevel(inner, ',').map((p) => p.trim());
    const name = parts[0] ?? '';
    const type = parts[1];
    const value = vars[name];
    if (!type) {
      out += value == null ? `{${name}}` : String(value);
      continue;
    }
    if (type === 'number') {
      out += typeof value === 'number' ? new Intl.NumberFormat(locale).format(value) : String(value ?? '');
      continue;
    }
    if (type === 'date' || type === 'time' || type === 'datetime') {
      if (value == null) continue;
      const d = toDate(value as Date | string | number);
      if (type === 'date') out += fmt?.formatDate ? fmt.formatDate(d) : d.toDateString();
      else if (type === 'time') out += fmt?.formatTime ? fmt.formatTime(d) : d.toTimeString();
      else out += fmt?.formatDateTime ? fmt.formatDateTime(d) : d.toISOString();
      continue;
    }
    const options = parseOptions(parts.slice(2).join(','));
    if (type === 'plural') {
      const n = typeof value === 'number' ? value : Number(value ?? 0);
      const rule = (fmt?.plural ?? ((x: number) => new Intl.PluralRules(locale).select(x)))(n);
      const branch = options[`=${n}`] ?? options[rule] ?? options.other ?? '';
      out += formatMessage(branch.replace(/#/g, new Intl.NumberFormat(locale).format(n)), vars, fmt);
      continue;
    }
    if (type === 'select') {
      const branch = options[String(value)] ?? options.other ?? '';
      out += formatMessage(branch, vars, fmt);
      continue;
    }
    out += String(value ?? '');
  }
  return out;
}

// --------------------------------------------------------------------------- provider

export interface IntlConfig {
  locale?: string;
  /** The lottery's timezone (e.g. `Africa/Lagos`): every displayed instant uses it (§15.4). */
  timeZone?: string;
  currency?: string;
  messages?: Messages;
}

export function createIntl(config: IntlConfig = {}): IntlFormatters {
  const locale = config.locale ?? 'en';
  const timeZone = config.timeZone ?? 'UTC';
  const messages = config.messages ?? {};
  const pr = new Intl.PluralRules(locale);
  const formatDate: IntlFormatters['formatDate'] = (d, opts) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone, ...opts }).format(toDate(d));
  const formatTime: IntlFormatters['formatTime'] = (d, opts) =>
    new Intl.DateTimeFormat(locale, { timeStyle: 'short', timeZone, ...opts }).format(toDate(d));
  const formatDateTime: IntlFormatters['formatDateTime'] = (d, opts) =>
    new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
      timeZoneName: 'short',
      ...opts,
    }).format(toDate(d));
  const formatters: IntlFormatters = {
    locale,
    timeZone,
    currency: config.currency,
    t: (key, vars, fallback) => formatMessage(messages[key] ?? fallback ?? key, vars, formatters),
    formatNumber: (n, opts) =>
      new Intl.NumberFormat(locale, opts).format(typeof n === 'string' ? (n as unknown as number) : n),
    formatMoney: (amountMinor, currency, opts) =>
      formatMoneyMinor(amountMinor, currency ?? config.currency ?? 'XTS', locale, opts),
    formatDate,
    formatTime,
    formatDateTime,
    formatRelative: (d, now = new Date()) => {
      const diff = toDate(d).getTime() - now.getTime();
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const abs = Math.abs(diff);
      if (abs < 60_000) return rtf.format(Math.round(diff / 1000), 'second');
      if (abs < 3_600_000) return rtf.format(Math.round(diff / 60_000), 'minute');
      if (abs < 86_400_000) return rtf.format(Math.round(diff / 3_600_000), 'hour');
      return rtf.format(Math.round(diff / 86_400_000), 'day');
    },
    plural: (n) => pr.select(n),
  };
  return formatters;
}

const IntlContext = createContext<IntlFormatters>(createIntl());

export function IntlProvider({ children, ...config }: IntlConfig & { children: ReactNode }) {
  const { locale, timeZone, currency, messages } = config;
  const value = useMemo(
    () => createIntl({ locale, timeZone, currency, messages }),
    [locale, timeZone, currency, messages],
  );
  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
}

export function useIntl(): IntlFormatters {
  return useContext(IntlContext);
}

/** Convenience for components: `t('key', vars, 'Fallback text')`. */
export function useT() {
  return useIntl().t;
}
